import React, { useEffect, useState } from "react";

export interface CvpColumn<T> {
  key: keyof T | string;
  header: string;
  render?: (item: T, index?: number) => React.ReactNode;
}

interface CvpTableProps<T> {
  data: T[];
  columns: CvpColumn<T>[];
  pageSize?: number;
  pageSizeOptions?: number[];
  page?: number;
  totalRecords?: number;
  hasMore?: boolean;
  loading?: boolean;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function CvpTable<T>({
  data,
  columns,
  pageSize = 50,
  pageSizeOptions = [25, 50, 100, 500],
  page = 1,
  totalRecords = 0,
  hasMore = true,
  loading = false,
  onPageChange,
  onPageSizeChange,
}: CvpTableProps<T>) {
  const [inputPage, setInputPage] = useState<string>(String(page));

  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  const maxRecords = totalRecords;
  const hasRecords = maxRecords > 0 && !loading;
  const totalPages = hasRecords ? Math.ceil(maxRecords / pageSize) : 1;

  // The global start index for record numbering labels
  const globalStartIndex = (page - 1) * pageSize;

  const displayStartIndex = hasRecords ? globalStartIndex + 1 : 0;
  const displayEndIndex = hasRecords
    ? Math.min(globalStartIndex + data.length, maxRecords)
    : 0;

  const canGoNext = hasRecords && page < totalPages && !loading;
  const canGoPrev = hasRecords && page > 1 && !loading;

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasRecords) return;

    let targetPage = parseInt(inputPage, 10);

    if (isNaN(targetPage) || targetPage < 1) {
      targetPage = 1;
    } else if (targetPage > totalPages) {
      targetPage = totalPages;
    }

    setInputPage(String(targetPage));
    if (targetPage !== page) {
      onPageChange?.(targetPage);
    }
  };

  return (
    <div style={{ fontFamily: "sans-serif", width: "100%" }}>
      <div
        style={{
          maxHeight: "2300px",
          overflowY: "auto",
          overflowX: "auto",
          border: "none",
          backgroundColor: "transparent",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            textAlign: "left",
          }}
        >
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={index}
                  style={{
                    padding: "12px 14px",
                    whiteSpace: "nowrap",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#e2e6d8",
                    zIndex: 10,
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#000000",
                    borderBottom: "1px solid #1a1a1a",
                  }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ padding: "20px", textAlign: "center" }}
                >
                  Loading records...
                </td>
              </tr>
            ) : data.length > 0 ? (
              data.map((row, rowIndex) => {
                const globalIndex = globalStartIndex + rowIndex;
                return (
                  <tr key={rowIndex}>
                    {columns.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        style={{
                          padding: "10px 14px",
                          whiteSpace: "nowrap",
                          borderBottom: "1px solid #d0d4c6",
                          fontSize: "14px",
                        }}
                      >
                        {col.render
                          ? col.render(row, globalIndex)
                          : String(row[col.key as keyof T] ?? "")}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ padding: "20px", textAlign: "center" }}
                >
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "16px",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "12px",
            color: "#000000",
            fontSize: "14px",
          }}
        >
          <span>
            Showing {displayStartIndex}–{displayEndIndex} of {maxRecords}{" "}
            Records
          </span>

          <span style={{ color: "#888888", fontWeight: "bold" }}>|</span>

          {/* Previous Button */}
          <button
            onClick={() => onPageChange?.(page - 1)}
            disabled={!canGoPrev}
            style={{
              backgroundColor: canGoPrev ? "#ffffff" : "transparent",
              color: canGoPrev ? "#000000" : "#a8a8a8",
              border: "1px solid #777777",
              borderRadius: "4px",
              padding: "4px 10px",
              cursor: canGoPrev ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              fontSize: "13px",
              boxShadow: canGoPrev ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
            }}
          >
            &#8592;
          </button>

          {/* Page Input Form */}
          <form
            onSubmit={handlePageInputSubmit}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <span>Page</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={hasRecords ? inputPage : "0"}
              disabled={!hasRecords}
              onChange={(e) => setInputPage(e.target.value)}
              onBlur={handlePageInputSubmit}
              style={{
                width: "48px",
                padding: "3px",
                textAlign: "center",
                backgroundColor: hasRecords ? "#ffffff" : "#f5f5f5",
                color: hasRecords ? "#000000" : "#888888",
                border: "1px solid #777777",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: hasRecords ? "text" : "not-allowed",
              }}
            />
            <span>of {hasRecords ? totalPages : 0}</span>
          </form>

          {/* Next Button */}
          <button
            onClick={() => onPageChange?.(page + 1)}
            disabled={!canGoNext}
            style={{
              backgroundColor: canGoNext ? "#ffffff" : "transparent",
              color: canGoNext ? "#000000" : "#a8a8a8",
              border: "1px solid #777777",
              borderRadius: "4px",
              padding: "4px 10px",
              cursor: canGoNext ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              fontSize: "13px",
              boxShadow: canGoNext ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
            }}
          >
            &#8594;
          </button>

          <span style={{ color: "#888888", fontWeight: "bold" }}>|</span>

          {/* Records Per Page Dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>Records Per Page</span>
            <select
              value={pageSize}
              disabled={!hasRecords}
              onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
              style={{
                backgroundColor: hasRecords ? "#ffffff" : "#f5f5f5",
                color: hasRecords ? "#000000" : "#888888",
                border: "1px solid #777777",
                borderRadius: "4px",
                padding: "4px 8px",
                cursor: hasRecords ? "pointer" : "not-allowed",
                fontSize: "14px",
              }}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
